/**
 * Build a TypeScript ambient-declaration string for the expression
 * bindings, typed from the node's sample input payload (ADR-0009 #4.3).
 *
 * Registered into Monaco's JavaScript defaults so that typing ``$json.``
 * in an expression field offers completions for the keys actually present
 * in the current item — IntelliSense driven by real data, not a static
 * schema. Pure (no Monaco import) so it unit-tests cheaply.
 *
 * See ``docs/topics/engines/expression-language.md`` §6.
 */

import { FORMULA_DEFS } from "@shared/formulas";

const MAX_DEPTH = 6;

/** Infer a TS type literal from a sample JSON value. Recursive; depth-
 *  capped so a pathological nested payload can't blow the string up.
 *  Unknown / null collapse to ``any`` (no completions, but no errors). */
export function inferTsType(value: unknown, depth = 0): string {
  if (depth > MAX_DEPTH) return "any";
  if (value === null || value === undefined) return "any";
  if (Array.isArray(value)) {
    if (value.length === 0) return "any[]";
    // Use the first element as the representative shape — payloads are
    // homogeneous in practice; a wrong guess just means fewer hints.
    return `Array<${inferTsType(value[0], depth + 1)}>`;
  }
  const t = typeof value;
  if (t === "string") return "string";
  if (t === "number") return "number";
  if (t === "boolean") return "boolean";
  if (t === "object") {
    const entries = Object.entries(value as Record<string, unknown>).map(
      ([k, v]) => `  ${JSON.stringify(k)}: ${inferTsType(v, depth + 1)};`,
    );
    if (entries.length === 0) return "Record<string, any>";
    return `{\n${entries.join("\n")}\n}`;
  }
  return "any";
}


/** TS shape of a ``$('Node')`` accessor (ADR-0012), with ``item.json`` /
 *  ``json`` / ``all()`` / ``first()`` / ``last()`` typed from the node's
 *  sample (ADR-0068 F4) — ``any`` when no sample is known. */
function nodeRefType(jsonT: string): string {
  return (
    "{ item: { json: " +
    jsonT +
    "; binary: Record<string, any>; meta: Record<string, any> };" +
    " json: " +
    jsonT +
    "; all(): " +
    jsonT +
    "[]; first(): " +
    jsonT +
    "; last(): " +
    jsonT +
    " }"
  );
}


/** Build the ambient ``.d.ts`` declaring every expression binding, with
 *  ``$json`` / ``$`` typed from the sample. ``$item`` / ``$items`` /
 *  ``$binary`` / ``$meta`` mirror the runtime surface.
 *
 *  ``nodeLabels`` (ADR-0012) enables ``$('Label')`` cross-node references:
 *  ``$`` is typed as the sample data **and** a callable taking the known
 *  upstream node labels, so typing ``$('`` offers the label completions
 *  and the returned accessor (.item / .all() / .first() / .last()).
 *
 *  ``nodeSamples`` (ADR-0068 F4) types each label's return SHAPE from that
 *  node's captured output — so ``$('Src').item.json.`` autocompletes the
 *  REAL columns, not ``any``. Emitted as per-label call SIGNATURES (most
 *  specific first) with a ``string`` fallback for a label with no sample. */
export function buildBindingsDts(
  sample: unknown,
  nodeLabels?: string[],
  nodeSamples?: Record<string, unknown>,
): string {
  const jsonT = inferTsType(sample);
  const labels = (nodeLabels ?? []).filter(Boolean);
  const samples = nodeSamples ?? {};
  let dollarT: string;
  if (labels.length) {
    // One typed call signature per known label + a generic `string` fallback,
    // intersected onto the sample data type (a callable object).
    const sigs = labels.map((l) => {
      const t = l in samples ? inferTsType(samples[l]) : "any";
      return `(label: ${JSON.stringify(l)}): ${nodeRefType(t)}`;
    });
    sigs.push(`(label: string): ${nodeRefType("any")}`);
    dollarT = `${jsonT} & { ${sigs.join("; ")} }`;
  } else {
    dollarT = `${jsonT} & ((label: string) => ${nodeRefType("any")})`;
  }
  return [
    "// Tray expression bindings — generated from the node's sample input.",
    `declare const $json: ${jsonT};`,
    `declare const $: ${dollarT};`,
    `declare const $item: { json: ${jsonT}; binary: Record<string, any>; meta: Record<string, any>; paired_item?: any };`,
    `declare const $items: Array<${jsonT}>;`,
    "declare const $binary: Record<string, any>;",
    "declare const $meta: Record<string, any>;",
    "",
    // Shared cross-runtime formula stdlib (ADR-0018) — the SAME functions the
    // app/page builder offers, evaluated server-side in QuickJS for workflows.
    "// Spreadsheet-style formula functions (familiar to non-developers).",
    ...FORMULA_DEFS.map((f) => `/** ${f.doc} */\n${f.decl}`),
    "",
  ].join("\n");
}


/** Parse a sample payload string (the editor's Input pane JSON) into a
 *  value for {@link buildBindingsDts}. Returns ``{}`` on blank/invalid
 *  input so callers always get a usable (if empty) binding surface.
 *  ``nodeLabels`` enables ``$('Label')`` completions (ADR-0012);
 *  ``nodeSamples`` types each label's columns (ADR-0068 F4). */
export function bindingsDtsFromSampleText(
  text: string,
  nodeLabels?: string[],
  nodeSamples?: Record<string, unknown>,
): string {
  let sample: unknown = {};
  const trimmed = (text ?? "").trim();
  if (trimmed) {
    try {
      sample = JSON.parse(trimmed);
    } catch {
      sample = {};
    }
  }
  return buildBindingsDts(sample, nodeLabels, nodeSamples);
}
